import React, { useCallback, useMemo } from "react";
import { FC } from "react";
import { getData } from "country-list";
import { useForm, useFormState } from "react-final-form";
import BootstrapInput from "../../../../../../../components/BootstrapInput";
import Autocomplete from "../../../../../../../components/Autocomplete";
import Field from "../../../../../../../components/Field";
import UploadFile from "../../../../../../../components/UploadFile/UploadFile";
import { AccordionList } from "../../../../../../../components/AccordionList";
import { teacherDetailsConfig } from "../../../../../../privateClasses/constants";

const Teacher: FC = () => {
  const { change } = useForm();
  const { values } = useFormState();

  const countries = useMemo(
    () =>
      getData().sort((a, b) =>
        a.name > b.name ? 1 : b.name > a.name ? -1 : 0,
      ),
    [],
  );

  const certificateFiles = useMemo(
    () => [...(values.certificates || []), ...(values.newCertificates || [])],
    [values.certificates, values.newCertificates],
  );

  const cvFiles = useMemo(() => {
    if (values.newCv) return [values.newCv];
    if (values.cv) return [values.cv];
    return [];
  }, [values.cv, values.newCv]);

  const addCertificateFiles = useCallback(
    async (newFiles: any[]) => {
      const current = values.newCertificates || [];
      const mapped = newFiles.map((file, i) => ({
        id: `new_${current.length + i + 1}`,
        name: file.name,
        size: file.size,
        file,
      }));
      change("newCertificates", [...current, ...mapped]);
    },
    [change, values.newCertificates],
  );

  const removeCertificateFile = useCallback(
    (file: any) => {
      if (String(file.id).startsWith("new_")) {
        const current: any[] = values.newCertificates || [];
        change(
          "newCertificates",
          current.filter((f) => f.id !== file.id),
        );
      } else {
        const current: any[] = values.certificates || [];
        change(
          "certificates",
          current.filter((f) => f.id !== file.id),
        );
      }
    },
    [change, values.newCertificates, values.certificates],
  );

  const addCvFile = useCallback(
    async (files: any[]) => {
      const file = files[0];
      change("newCv", {
        id: "new_cv",
        name: file.name,
        size: file.size,
        file,
      });
    },
    [change],
  );

  const removeCvFile = useCallback(() => {
    change("newCv", null);
    change("cv", null);
  }, [change]);

  return (
    <>
      <div>
        <AccordionList
          fieldName="teacherProfile.details"
          configs={teacherDetailsConfig}
        />
      </div>
      <div>
        <UploadFile
          uploadedFiles={certificateFiles}
          addNewFiles={addCertificateFiles}
          removeFile={removeCertificateFile}
          titleText={"Upload Certificates"}
          subText={"Drag & Drop Your Certificates"}
          allowedFileTypes={["pdf", "jpg", "jpeg", "png"]}
          maxSize={5}
          maxFiles={5}
        />
      </div>
      <div>
        <UploadFile
          required
          name="cv"
          uploadedFiles={cvFiles}
          addNewFiles={addCvFile}
          removeFile={removeCvFile}
          titleText={"Upload CV"}
          subText={"Drag & Drop Your CV"}
          allowedFileTypes={["pdf", "jpg", "jpeg", "png"]}
          maxSize={5}
          maxFiles={1}
        />
      </div>
      <Field
        required
        fullWidth
        type="text"
        name="name"
        label="Full Name"
        placeholder="Full Name"
        component={BootstrapInput}
      />
      <Field
        required
        fullWidth
        name="teacherProfile.country"
        label="Country"
        options={countries}
        placeholder="Country"
        component={Autocomplete}
      />
    </>
  );
};

export default React.memo(Teacher);
